import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "text-textPrimary text-left" }
const _hoisted_2 = { class: "flex items-start justify-between gap-4" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold" }, "Status", -1)
const _hoisted_4 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold" }, "Solicitação automática", -1)
const _hoisted_6 = {
  key: 0,
  "data-testid": "ds-attendance-shipment-request-overdue",
  class: "inline-flex items-center rounded-full border border-negative bg-red-50 px-2 py-0.5 text-[10px] font-bold text-negative whitespace-nowrap",
  title: "O prazo da política passou. A solicitação continua aberta — vencer não bloqueia aprovação, edição nem pagamento."
}
const _hoisted_7 = { class: "mt-2 flex flex-col gap-0.5 text-sm" }
const _hoisted_8 = { "data-testid": "ds-attendance-shipment-request-leg" }
const _hoisted_9 = { "data-testid": "ds-attendance-shipment-request-channel" }
const _hoisted_10 = {
  key: 0,
  "data-testid": "ds-attendance-shipment-request-deadline"
}
const _hoisted_11 = {
  key: 1,
  "data-testid": "ds-attendance-shipment-request-opened-at"
}
const _hoisted_12 = /*#__PURE__*/_createElementVNode("p", { class: "mt-2 text-xs text-neutral50" }, " Cancelada sozinha se chegar folha aprovada, check tardio do app, App Externo ou envio manual que cubra a perna. ", -1)
const _hoisted_13 = { class: "flex w-1/2 justify-between my-4" }
const _hoisted_14 = { class: "flex flex-col gap-4" }
const _hoisted_15 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold" }, "Data", -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold" }, "Escalas", -1)
const _hoisted_17 = { class: "flex flex-col gap-4" }
const _hoisted_18 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold" }, "Unidade", -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("p", { class: "font-bold" }, "Turnos", -1)
const _hoisted_20 = { class: "flex justify-end gap-4" }

import { ref, computed, onMounted, defineAsyncComponent } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import StatusBadge from '@/components/designSystem/Badges/StatusBadge.vue'
import DsAttendanceHistoryCard, {
  HistoryEvent
} from '@/components/designSystem/Cards/DsAttendanceHistoryCard.vue'
import CommentsForm from '@/components/designSystem/Inputs/CommentsForm.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import ConfirmDeleteAction from '@/components/designSystem/modals/confirm.delete.action.vue'
import {
  ACTIVITY_REQUEST_CHANNEL_LABELS,
  ActivityRequestChannel,
  CoveredLegs,
  DSAttendanceStatus,
  REQUESTED_LEG_LABELS
} from '@/constants/ds-attendance.constants'
import { mapAuditTrailToHistoryEvents } from '@/utils/dsAttendanceHistory'
import type { DSAttendanceContainerDetailResponse } from '@/Network/Types/Responses/DsAttendancePolicyResponses'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import {
  approvalsQueueBreadcrumbs,
  approvalsQueueLocation
} from '@/modules/company/Approvals/Hub/queues'
import { buildSheetHandoff } from '@/components/designSystem/modals/SendRecordModal/types'
import type {
  RedirectToSheetPayload,
  SendRecordContext,
  SendRecordStationOption
} from '@/components/designSystem/modals/SendRecordModal/types'
import type { DsAttendanceUploadForm } from '@/components/designSystem/modals/AttendanceUploadModal/types'
import { DEFAULT_UTC_OFFSET, formatDateTimeWithOffset } from '@/utils/date'
import { toDDMMYYYY } from '../utils/activity-time'
import { routeNameForStatus } from '../utils/status-route-map'
import { useNextContainer } from '../composables/useNextContainer'
import { useCanEmitAttendance } from '../composables/useCanEmitAttendance'
import { useCanRevertValidation } from '../composables/useCanRevertValidation'

interface AwaitingShipmentDetail extends DSAttendanceContainerDetailResponse {
  requestedLeg?: CoveredLegs | string | null
  requestChannel?: ActivityRequestChannel | string | null
  /** Prazo da cobrança — a F3 reaproveita o "vencido" do container (§6.4), sem campo novo. */
  expectedSubmissionAt?: string | null
  /** Quando o job abriu a cobrança: a prova de que quem abriu foi o sistema. */
  createdAt?: string | null
  companyId?: number | null
  /**
   * O campo REAL do contrato: `ContainerDetailResponseDto` devolve a vaga como escalar no
   * topo (`doctorStationId`), lido direto da coluna do container — e por isso preenchido
   * também numa solicitação, que nasce SEM entry (§6.4). O objeto `doctorStation` abaixo é a
   * forma rica que a spec §3.4 prevê e o backend ainda não serializa: quando chegar, os dados
   * do plantão (médico, horários) enriquecem o modal; até lá, o id sozinho já o abre.
   */
  doctorStationId?: number | null
  doctorStation?: {
    id?: number | null
    doctorName?: string | null
    doctorCrm?: string | null
    date?: string | null
    shiftName?: string | null
    expectedStart?: string | null
    expectedEnd?: string | null
    healthUnitName?: string | null
    scheduleName?: string | null
    /** Offset do plantão (`-03:00`), para ler `createdAt` — que é instante UTC, não parede. */
    timezone?: string | null
  } | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DsAttendanceAwaitingShipment',
  setup(__props) {

const SendRecordModal = defineAsyncComponent(
  () => import('@/components/designSystem/modals/SendRecordModal/Index.vue')
)
const AttendanceUploadModal = defineAsyncComponent(
  () => import('@/components/designSystem/modals/AttendanceUploadModal/Index.vue')
)

/**
 * F3 (§6.4) — os campos da solicitação automática no detalhe do container. Ficam num tipo
 * LOCAL porque `DsAttendancePolicyResponses.ts` está fora da lista de arquivos desta task
 * (mesma escolha da T9 no service e da F2 em `DsAttendanceActivityAudit.vue`): quando o
 * contrato do backend estabilizar, é um move mecânico.
 *
 * Todos opcionais: a tela precisa continuar de pé em backend anterior à F3.
 */
const router = useRouter()
const headerStore = useHeaderStore()
const route = useRoute()
const toast = useToast()
const { notifyError } = useErrorToast()

const details = ref<AwaitingShipmentDetail | null>(null)
const isDeleted = computed(() => details.value?.status === DSAttendanceStatus.DELETED)

/**
 * F3 — é uma cobrança quando a política pediu uma perna. `requestedLeg` sozinho basta: esta
 * rota já é a do container aguardando envio, e nenhum backend anterior à F3 devolve o campo.
 */
const isAutomaticRequest = computed(() => !!details.value?.requestedLeg)

/** Catálogo local primeiro; código fora dele aparece cru — rótulo vazio esconderia a cobrança. */
const requestedLegLabel = computed(() => {
  const leg = details.value?.requestedLeg
  return (leg ? REQUESTED_LEG_LABELS[leg] : null) ?? leg ?? 'Perna não informada'
})

const requestChannelLabel = computed(() => {
  const channel = details.value?.requestChannel
  return (
    ACTIVITY_REQUEST_CHANNEL_LABELS[channel as ActivityRequestChannel] ??
    channel ??
    'Canal não informado'
  )
})

/**
 * Offset do plantão. O escalar do topo (`details.timezone`) é o campo que
 * `DSAttendanceContainerDetailResponseDto` REALMENTE serve — o objeto rico
 * `doctorStation.timezone` é a forma que a spec §3.4 prevê e que o backend ainda não
 * serializa (comentário acima, na `interface`). Ler só o objeto rico fazia o prazo cair
 * SEMPRE em `-03:00` fora de Brasília, 1–2 h divergente do card da fila (achado Opus
 * W-N1); Brasília entra por último, só quando nem o container nem o objeto rico têm nada.
 */
const stationOffset = computed(
  () => details.value?.timezone ?? details.value?.doctorStation?.timezone ?? DEFAULT_UTC_OFFSET
)

/*
 * R2-I4 (achado Opus IMPORTANT-1) — `expectedSubmissionAt` é um INSTANTE real: nasce de
 * `calculateExpectedSubmissionAtTzAware` (`zonedTimeToUtc(23:59 local do plantão)` + horas),
 * não de `station.end_hour`, e a API o serve cru (`toISOString()`). Lido como parede
 * literal, como esta tela fazia, o mesmo prazo de 10:00 em Brasília aparecia 10:00 no card
 * da fila e 13:00 aqui — e "Vencida" acendia em horas diferentes nas duas. A régua é a
 * mesma do `DsAttendanceRequestCard`: o fuso do plantão.
 */
const formattedDeadline = computed(() =>
  details.value?.expectedSubmissionAt
    ? formatDateTimeWithOffset(details.value.expectedSubmissionAt, stationOffset.value)
    : ''
)

/*
 * `createdAt` (N1): `created_at` é `@CreateDateColumn` do `BaseModel`, um instante REAL em
 * UTC. Lido como parede, uma cobrança aberta às 16:07 na unidade aparecia como 19:07. Aqui
 * vale o offset do plantão, como o `DsAttendanceConciliationModal` e a
 * `StationActivityRecordsSection` já fazem.
 */
const formattedOpenedAt = computed(() => {
  const iso = details.value?.createdAt
  return iso ? formatDateTimeWithOffset(iso, stationOffset.value) : ''
})

/** Vencer NÃO bloqueia nada (§6): é prioridade visual, não trava. */
const isRequestOverdue = computed(() => {
  const iso = details.value?.expectedSubmissionAt
  if (!iso) return false
  const deadline = new Date(iso).getTime()
  return !Number.isNaN(deadline) && deadline <= Date.now()
})

const formattedDate = computed(
  () => toDDMMYYYY(details.value?.referenceDate) || details.value?.referenceDate || 'Não informada'
)

const isDeleteModalOpen = ref(false)
const isDeleting = ref(false)
const historyEvents = ref<HistoryEvent[]>([])

/**
 * Eixo 4 — "Próximo registro". `hasNextContainer` é resolvido uma vez ao abrir a tela: a fila
 * pode mudar embaixo, e é por isso que `goToNext` consulta o backend de novo no clique.
 */
const hasNextContainer = ref(false)
const isGoingToNext = ref(false)
const { fetchNextId, goToNext } = useNextContainer()

async function handleGoToNext() {
  if (isGoingToNext.value) return
  isGoingToNext.value = true
  try {
    const moved = await goToNext(String(route.params.attendanceId))
    // Fila acabou entre a abertura da tela e o clique: volta para a lista em vez de deixar o
    // auditor olhando um botão morto.
    if (!moved) {
      hasNextContainer.value = false
      router.push(approvalsQueueLocation('attendance'))
    }
  } finally {
    isGoingToNext.value = false
  }
}

/**
 * F3 — o header default fala de folha de ponto; uma cobrança de Registro de Atividade não é
 * folha. Só troca depois do fetch, e só quando `requestedLeg` veio: sem ele o header é o de
 * hoje, palavra por palavra.
 */
function applyHeader() {
  if (isAutomaticRequest.value) {
    headerStore.setTitle(
      'Solicitação de Registro de Atividade',
      'Solicitação automática aguardando o envio do registro',
      approvalsQueueBreadcrumbs('attendance')
    )
    return
  }
  headerStore.setTitle(
    'Envio de Folha de Ponto',
    'Registro aguardando o envio da folha de monitoramento',
    approvalsQueueBreadcrumbs('attendance')
  )
}

async function loadDetails(attendanceId: string) {
  try {
    const response = await Network.dsAttendance.getById(attendanceId)
    details.value = response as AwaitingShipmentDetail
  } catch (error) {
    console.error('Erro ao buscar detalhes do container:', error)
    notifyError(error, 'Não foi possível carregar os detalhes do registro de presença.')
  }
  applyHeader()
}

async function loadHistory(attendanceId: string) {
  try {
    const response = await Network.dsAttendance.getAuditTrail(attendanceId)
    historyEvents.value = mapAuditTrailToHistoryEvents(response.data)
  } catch (error) {
    // Antes só o console sabia: o card de histórico ficava vazio como se o registro
    // não tivesse eventos, sem distinguir "sem histórico" de "falhou ao buscar".
    console.error('Erro ao buscar histórico do container:', error)
    notifyError(error, 'Não foi possível carregar o histórico do registro de presença.')
  }
}

onMounted(async () => {
  // Sem isto a tela herdava o header da página anterior (título e breadcrumb errados).
  applyHeader()
  const attendanceId = String(route.params.attendanceId)

  await loadDetails(attendanceId)

  // Não bloqueia o carregamento da tela: `fetchNextId` já engole a falha e devolve `null`.
  hasNextContainer.value = (await fetchNextId(attendanceId)) !== null

  await loadHistory(attendanceId)
})

/* -------------------------------------------------------------------------- *
 * F3 — responder à cobrança sem sair da tela
 * -------------------------------------------------------------------------- */

const isSendRecordModalOpen = ref(false)
const isSheetUploadModalOpen = ref(false)
const sheetInitialForm = ref<Partial<DsAttendanceUploadForm> | null>(null)
const sheetLockedFields = ref<string[]>([])

/**
 * A vaga cobrada, no formato que o modal da W4 espera. `null` quando o detalhe não traz a
 * vaga (backend anterior à F3): sem `doctorStationId` o modal abriria com `0` e o passo 2
 * ficaria sem saída (I7) — melhor um botão desabilitado com o motivo do que um beco.
 */
function buildSendRecordStation(
  detail: AwaitingShipmentDetail,
  doctorStationId: number
): SendRecordStationOption {
  // Campos ausentes ficam `undefined` de propósito: `SendRecordStationOption` os declara
  // opcionais, e normalizar tudo para `null` só somaria ramos sem mudar o que o modal lê.
  const station = detail.doctorStation ?? {}
  return {
    doctorStationId,
    referenceDate: station.date ?? detail.referenceDate,
    expectedStart: station.expectedStart,
    expectedEnd: station.expectedEnd,
    doctorName: station.doctorName,
    scheduleName: station.scheduleName ?? detail.schedule?.name,
    healthUnitName: station.healthUnitName ?? detail.healthUnit?.name,
    shiftName: station.shiftName,
    // A perna cobrada é literalmente a perna que falta — é ela que o passo 4 pré-marca.
    missingLegs: (detail.requestedLeg as CoveredLegs) ?? null
  }
}

const sendRecordContext = computed<SendRecordContext | null>(() => {
  const detail = details.value
  // O escalar do topo primeiro: é o que o backend realmente devolve hoje. O `doctorStation.id`
  // fica como segunda leitura para o dia em que o objeto rico da §3.4 chegar.
  const doctorStationId = Number(detail?.doctorStationId ?? detail?.doctorStation?.id ?? 0)
  const scheduleId = Number(detail?.schedule?.id ?? 0)
  if (!detail || !doctorStationId || !scheduleId) return null

  return {
    doctorStationId,
    scheduleId,
    companyId: detail.companyId ?? null,
    healthUnitId: detail.healthUnit?.id ?? null,
    referenceDate: detail.referenceDate ?? null,
    station: buildSendRecordStation(detail, doctorStationId)
  }
})

/**
 * Enviar registro exige `attendance_emitting`, nos três recursos de presença —
 * mesmo critério de `canEmitSheetRecord` na fila e de `canEmitActivityRecord` no
 * Detalhes do Plantão. Esta tela é o segundo caminho até o wizard de folha: o
 * passo 1 do `SendRecordModal` bifurca para ele (C6), e nem o modal nem o wizard
 * checam permissão.
 */
const canEmitRecord = useCanEmitAttendance()
const canRevertValidation = useCanRevertValidation()

const sendRecordTooltip = computed(() => {
  if (!canEmitRecord.value) return 'Você não tem permissão para enviar registros.'
  if (!sendRecordContext.value)
    return 'Envie pelo Detalhes do Plantão: este registro não traz a vaga de origem.'
  return undefined
})

function openSendRecord() {
  if (!canEmitRecord.value || !sendRecordContext.value) return
  isSendRecordModalOpen.value = true
}

/**
 * Recarrega detalhe e histórico e diz se o container saiu de "aguardando envio". O backend
 * responde **201** e, quando o plantão tinha solicitação aberta, é ESTE container que é
 * reusado: vira `PENDING_AUDIT` com `CONTAINER_SUBMIT` + `REQUEST_FULFILLED` na trilha (§6.5).
 *
 * Se o `coveredLegs` enviado não cobrir a perna cobrada, o container CONTINUA
 * `PENDING_SUBMISSION` (§6.5, fim) e a tela permanece onde está, agora com o histórico novo.
 */
async function reloadAfterSubmission(): Promise<string | null> {
  const attendanceId = String(route.params.attendanceId)
  await loadDetails(attendanceId)
  await loadHistory(attendanceId)

  const routeName = routeNameForStatus(details.value?.status ?? null)
  return routeName && routeName !== 'DsAttendancePendingSubmission' ? routeName : null
}

/** Rota do novo status, guardada até o modal fechar (ver `handleSendRecordSaved`). */
const pendingRouteAfterSend = ref<string | null>(null)

function leaveTo(routeName: string) {
  router.push({
    name: routeName,
    params: { attendanceId: String(route.params.attendanceId) },
    query: { ...route.query }
  })
}

/**
 * Envio aceito pelo `SendRecordModal`. O modal emite `saved` JUNTO com a transição para
 * `SEND_RECORD_STEP.SUCCESS` — a tela que a §8.2 exige, com os `#A-` criados e os botões
 * "Voltar à escala" / "Ver na fila de auditoria". Fechar o modal aqui matava essa tela: o
 * auditor via só um toast e caía na fila sem nunca ver o número do registro criado. Então
 * recarregamos por baixo e adiamos a navegação para o fechamento — o mesmo contrato do
 * `stationDetailsModal` do Detalhes do Plantão (F2), que só recarrega os dados.
 */
async function handleSendRecordSaved() {
  pendingRouteAfterSend.value = await reloadAfterSubmission()
}

/** Fechou o modal: agora sim a tela segue para a rota do novo status, com os filtros junto. */
function handleSendRecordModalToggle(open: boolean) {
  isSendRecordModalOpen.value = open
  if (open) return

  const routeName = pendingRouteAfterSend.value
  pendingRouteAfterSend.value = null
  if (routeName) leaveTo(routeName)
}

/**
 * Folha enviada pelo wizard do C6. Este modal não tem tela de sucesso própria: fecha, avisa
 * e segue, que é o comportamento de sempre do "Registro de Plantão".
 */
async function handleSheetSaved() {
  isSheetUploadModalOpen.value = false
  toast.success('Registro enviado com sucesso.')

  const routeName = await reloadAfterSubmission()
  if (routeName) leaveTo(routeName)
}

/** C6 — o passo 1 bifurca: "Registro de Plantão" reabre o wizard de folha aqui mesmo. */
function handleRedirectToSheet(payload: RedirectToSheetPayload) {
  isSendRecordModalOpen.value = false
  // O wizard de folha não checa permissão em passo nenhum: quem gateia é quem abre.
  if (!canEmitRecord.value) return
  const handoff = buildSheetHandoff(payload)
  sheetInitialForm.value = handoff.form
  sheetLockedFields.value = handoff.lockedFields
  isSheetUploadModalOpen.value = true
}

async function handleDeleteConfirm(justification?: string) {
  const attendanceId = String(route.params.attendanceId)
  isDeleting.value = true
  try {
    await Network.dsAttendance.deleteContainer(attendanceId, { justification: justification! })
    isDeleteModalOpen.value = false
    toast.success('Registro excluído com sucesso.')
    router.push(approvalsQueueLocation('attendance'))
  } catch (error) {
    console.error('Erro ao excluir o registro:', error)
    notifyError(error, 'Não foi possível excluir o registro de presença.')
  } finally {
    isDeleting.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", null, [
          _hoisted_3,
          _createVNode(StatusBadge, {
            status: (details.value?.status as DSAttendanceStatus) ?? _unref(DSAttendanceStatus).PENDING_SUBMISSION
          }, null, 8, ["status"])
        ]),
        (hasNextContainer.value)
          ? (_openBlock(), _createBlock(OutlineButton, {
              key: 0,
              "data-testid": "ds-attendance-next-container-button",
              text: "Próximo registro ›",
              width: "180px",
              height: "40px",
              loading: isGoingToNext.value,
              onClick: handleGoToNext
            }, null, 8, ["loading"]))
          : _createCommentVNode("", true)
      ]),
      (isAutomaticRequest.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            "data-testid": "ds-attendance-shipment-request",
            class: _normalizeClass(["my-4 rounded-lg border p-4", isRequestOverdue.value ? 'border-negative bg-red-50' : 'border-alert bg-orange-50'])
          }, [
            _createElementVNode("div", _hoisted_4, [
              _hoisted_5,
              (isRequestOverdue.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_6, " Vencida "))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("span", _hoisted_8, [
                _createTextVNode(" Perna cobrada: "),
                _createElementVNode("b", null, _toDisplayString(requestedLegLabel.value), 1)
              ]),
              _createElementVNode("span", _hoisted_9, [
                _createTextVNode(" Canal esperado: "),
                _createElementVNode("b", null, _toDisplayString(requestChannelLabel.value), 1)
              ]),
              (formattedDeadline.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_10, [
                    _createTextVNode(" Prazo da política: "),
                    _createElementVNode("b", null, "até " + _toDisplayString(formattedDeadline.value), 1)
                  ]))
                : _createCommentVNode("", true),
              (formattedOpenedAt.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                    _createTextVNode(" Aberta em "),
                    _createElementVNode("b", null, _toDisplayString(formattedOpenedAt.value), 1)
                  ]))
                : _createCommentVNode("", true)
            ]),
            _hoisted_12
          ], 2))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_13, [
        _createElementVNode("div", _hoisted_14, [
          _createElementVNode("div", null, [
            _hoisted_15,
            _createElementVNode("p", null, _toDisplayString(formattedDate.value), 1)
          ]),
          _createElementVNode("div", null, [
            _hoisted_16,
            _createElementVNode("p", null, _toDisplayString(details.value?.schedule?.name ?? 'Não informada'), 1)
          ])
        ]),
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("div", null, [
            _hoisted_18,
            _createElementVNode("p", null, _toDisplayString(details.value?.healthUnit?.name ?? 'Não informada'), 1)
          ]),
          _createElementVNode("div", null, [
            _hoisted_19,
            _createElementVNode("p", null, _toDisplayString(details.value?.shifts?.length
                ? details.value.shifts.map((s) => s.name).join(', ')
                : 'Não informado'), 1)
          ])
        ])
      ]),
      _createVNode(DsAttendanceHistoryCard, {
        title: "Histórico",
        events: historyEvents.value
      }, null, 8, ["events"]),
      _createVNode(CommentsForm, {
        "attendance-id": String(_unref(route).params.attendanceId),
        "can-comment": _unref(canEmitRecord)
      }, null, 8, ["attendance-id", "can-comment"]),
      _createElementVNode("div", _hoisted_20, [
        _createVNode(FilledButton, {
          text: "Fechar",
          width: "80px",
          height: "48px",
          class: "!bg-primary30",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push(_unref(approvalsQueueLocation)('attendance'))))
        }),
        (isAutomaticRequest.value)
          ? (_openBlock(), _createBlock(OutlineButton, {
              key: 0,
              "data-testid": "ds-attendance-shipment-send-record",
              text: "Enviar registro",
              width: "180px",
              height: "48px",
              disabled: !_unref(canEmitRecord) || !sendRecordContext.value,
              title: sendRecordTooltip.value,
              onClick: openSendRecord
            }, null, 8, ["disabled", "title"]))
          : _createCommentVNode("", true),
        (!isDeleted.value && _unref(canRevertValidation))
          ? (_openBlock(), _createBlock(OutlineButton, {
              key: 1,
              text: "Excluir pendência",
              width: "180px",
              height: "48px",
              class: "!border-negative !text-negative !bg-white",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (isDeleteModalOpen.value = true))
            }))
          : _createCommentVNode("", true)
      ])
    ]),
    (isSendRecordModalOpen.value && sendRecordContext.value)
      ? (_openBlock(), _createBlock(_unref(SendRecordModal), {
          key: 0,
          "model-value": isSendRecordModalOpen.value,
          context: sendRecordContext.value,
          "doctor-station-id": sendRecordContext.value.doctorStationId,
          "schedule-id": sendRecordContext.value.scheduleId,
          "onUpdate:modelValue": handleSendRecordModalToggle,
          onSaved: handleSendRecordSaved,
          onRedirectToSheet: handleRedirectToSheet
        }, null, 8, ["model-value", "context", "doctor-station-id", "schedule-id"]))
      : _createCommentVNode("", true),
    (isSheetUploadModalOpen.value)
      ? (_openBlock(), _createBlock(_unref(AttendanceUploadModal), {
          key: 1,
          modelValue: isSheetUploadModalOpen.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isSheetUploadModalOpen).value = $event)),
          "initial-form": sheetInitialForm.value,
          "locked-fields": sheetLockedFields.value,
          onSave: handleSheetSaved
        }, null, 8, ["modelValue", "initial-form", "locked-fields"]))
      : _createCommentVNode("", true),
    _createVNode(ConfirmDeleteAction, {
      show: isDeleteModalOpen.value,
      isLoading: isDeleting.value,
      showJustification: true,
      title: "Excluir pendência",
      description: "Você tem certeza que deseja excluir essa pendência? Essa ação não poderá ser desfeita.",
      textButton: "Excluir pendência",
      onCloseModal: _cache[3] || (_cache[3] = ($event: any) => (isDeleteModalOpen.value = false)),
      onHandleConfirm: handleDeleteConfirm
    }, null, 8, ["show", "isLoading"])
  ], 64))
}
}

})